'use client'

import { useRef, useEffect, useCallback, useState, useMemo } from 'react'
import { drawVenueShape } from './venue-shapes'
import { drawRoundedPolygon } from '@/lib/seat-map/canvas-utils'
import { sectionParts } from '@/lib/seat-map/section-parts'
import type {
  ChartLayoutData,
  LayoutSection,
  SectionOverview,
  SeatCategory,
  VenueShape,
} from '@/types/seating'

interface SeatMapMinimapProps {
  layout: ChartLayoutData
  sections: SectionOverview[]
  categories: SeatCategory[]
  focusedSectionKey: string | null
  venueShape?: VenueShape
  transform: { x: number; y: number; scale: number }
  containerWidth: number
  containerHeight: number
  onNavigate: (x: number, y: number) => void
  visible: boolean
  theme?: 'dark' | 'light'
}

// Kept small so it never hides much of the map it summarises
const MINIMAP_W = 160
const MINIMAP_H = 120

function buildArcPath(
  ctx: CanvasRenderingContext2D,
  arcConfig: NonNullable<LayoutSection['arcConfig']>
) {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle } = arcConfig
  const startRad = (startAngle * Math.PI) / 180
  const endRad = (endAngle * Math.PI) / 180
  ctx.beginPath()
  ctx.arc(cx, cy, outerRadius, startRad, endRad, false)
  ctx.arc(cx, cy, innerRadius, endRad, startRad, true)
  ctx.closePath()
}

export default function SeatMapMinimap({
  layout,
  sections,
  categories,
  focusedSectionKey,
  venueShape,
  transform,
  containerWidth,
  containerHeight,
  onNavigate,
  visible,
  theme,
}: SeatMapMinimapProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // Memoised: rebuilt maps made `draw` a new function on every render.
  const categoryMap = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories])
  const sectionMap = useMemo(() => new Map(sections.map(s => [s.sectionKey, s])), [sections])

  // Detect theme: explicit prop > data-theme attribute > default light
  const resolvedTheme = theme || (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light')
  const isDark = resolvedTheme === 'dark'

  const draw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Hidden by CSS (`hidden sm:block`: every phone) — nothing would show,
    // yet it repainted the whole venue on each frame of a pan.
    if (canvas.getClientRects().length === 0) return

    const dpr = window.devicePixelRatio || 1
    canvas.width = MINIMAP_W * dpr
    canvas.height = MINIMAP_H * dpr
    canvas.style.width = `${MINIMAP_W}px`
    canvas.style.height = `${MINIMAP_H}px`
    ctx.scale(dpr, dpr)

    // Background — theme-aware
    ctx.fillStyle = isDark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)'
    ctx.fillRect(0, 0, MINIMAP_W, MINIMAP_H)

    // Scale factor to fit layout in minimap
    const scaleX = MINIMAP_W / layout.width
    const scaleY = MINIMAP_H / layout.height
    const scale = Math.min(scaleX, scaleY) * 0.85
    const offsetX = (MINIMAP_W - layout.width * scale) / 2
    const offsetY = (MINIMAP_H - layout.height * scale) / 2

    ctx.save()
    ctx.translate(offsetX, offsetY)
    ctx.scale(scale, scale)

    // Venue shape (tiny)
    if (venueShape) {
      ctx.globalAlpha = 0.3
      drawVenueShape(ctx, venueShape)
      ctx.globalAlpha = 1
    }

    // Stage
    if (layout.stage) {
      const s = layout.stage
      ctx.fillStyle = isDark ? '#334155' : '#1e293b'
      if (s.shape === 'circle' && s.cx != null && s.radius != null) {
        ctx.beginPath()
        ctx.arc(s.cx, s.cy!, s.radius, 0, Math.PI * 2)
        ctx.fill()
      } else {
        ctx.fillRect(s.x, s.y, s.width, s.height)
      }
    }

    // Sections
    for (const ls of layout.sections) {
      const sd = sectionMap.get(ls.key)
      if (!sd) continue
      if (sd.sectionType === 'STAGE') continue

      const cat = sd.categoryId ? categoryMap.get(sd.categoryId) : null
      const color = cat?.color || '#94a3b8'
      const isFocused = focusedSectionKey === ls.key

      ctx.fillStyle = color
      ctx.globalAlpha = isFocused ? 0.9 : 0.5

      // Every part of the section: a multi-place GA zone shows all its blocks.
      for (const part of sectionParts(ls)) {
        if (part.shape === 'arc' && part.arcConfig) {
          buildArcPath(ctx, part.arcConfig)
          ctx.fill()
          if (isFocused) {
            ctx.strokeStyle = '#3b82f6'
            ctx.lineWidth = 3.5
            buildArcPath(ctx, part.arcConfig)
            ctx.stroke()
          }
        } else if (part.shape === 'polygon' && part.points && part.points.length >= 3) {
          // Same bowed outline the main map draws (edgeCurves/edgeSkew)
          drawRoundedPolygon(ctx, part.points, 0, part.edgeCurves, part.edgeSkew)
          ctx.fill()
          if (isFocused) {
            ctx.strokeStyle = '#3b82f6'
            ctx.lineWidth = 3.5
            ctx.stroke()
          }
        } else {
          ctx.fillRect(part.x, part.y, part.width, part.height)
          if (isFocused) {
            ctx.strokeStyle = '#3b82f6'
            ctx.lineWidth = 3.5
            ctx.strokeRect(part.x, part.y, part.width, part.height)
          }
        }
      }
    }

    ctx.globalAlpha = 1
    ctx.restore()

    // Viewport indicator — dashed blue rectangle
    const vpLeft = -transform.x / transform.scale
    const vpTop = -transform.y / transform.scale
    const vpWidth = containerWidth / transform.scale
    const vpHeight = containerHeight / transform.scale

    ctx.strokeStyle = '#3b82f6'
    ctx.lineWidth = 2
    ctx.setLineDash([4, 3])
    ctx.strokeRect(
      offsetX + vpLeft * scale,
      offsetY + vpTop * scale,
      vpWidth * scale,
      vpHeight * scale
    )
    ctx.setLineDash([])

    // Zoom percentage label
    const zoomPct = Math.round(transform.scale * 100)
    ctx.fillStyle = isDark ? '#94a3b8' : '#64748b'
    ctx.font = '600 10px Inter, system-ui, sans-serif'
    ctx.textAlign = 'right'
    ctx.textBaseline = 'bottom'
    ctx.fillText(`${zoomPct}%`, MINIMAP_W - 6, MINIMAP_H - 4)
  }, [layout, sections, categories, focusedSectionKey, venueShape, transform, containerWidth, containerHeight, sectionMap, categoryMap, isDark])

  useEffect(() => {
    if (visible) draw()
  }, [draw, visible])

  // Convert minimap pixel coords to world coords and navigate
  const navigateToMinimapPoint = useCallback((clientX: number, clientY: number) => {
    const rect = canvasRef.current?.getBoundingClientRect()
    if (!rect) return

    const scaleX = MINIMAP_W / layout.width
    const scaleY = MINIMAP_H / layout.height
    const scale = Math.min(scaleX, scaleY) * 0.85
    const offsetX = (MINIMAP_W - layout.width * scale) / 2
    const offsetY = (MINIMAP_H - layout.height * scale) / 2

    const clickX = clientX - rect.left
    const clickY = clientY - rect.top

    const worldX = (clickX - offsetX) / scale
    const worldY = (clickY - offsetY) / scale

    const newX = -(worldX * transform.scale - containerWidth / 2)
    const newY = -(worldY * transform.scale - containerHeight / 2)

    onNavigate(newX, newY)
  }, [layout.width, layout.height, transform.scale, containerWidth, containerHeight, onNavigate])

  const isDraggingRef = useRef(false)

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isDraggingRef.current = true
    navigateToMinimapPoint(e.clientX, e.clientY)
  }, [navigateToMinimapPoint])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDraggingRef.current) return
    navigateToMinimapPoint(e.clientX, e.clientY)
  }, [navigateToMinimapPoint])

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false
  }, [])

  // Release drag if mouse leaves the minimap
  const handleMouseLeave = useCallback(() => {
    isDraggingRef.current = false
  }, [])

  // On a short screen (landscape phone) the minimap would cover a third of
  // the map, so it stays hidden there.
  const [shortViewport, setShortViewport] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(max-height: 499px)')
    const update = () => setShortViewport(mq.matches)
    update()
    mq.addEventListener?.('change', update)
    return () => mq.removeEventListener?.('change', update)
  }, [])

  if (!visible || shortViewport) return null

  return (
    <div className={`absolute top-4 right-16 z-10 rounded-xl shadow-lg overflow-hidden backdrop-blur-sm cursor-pointer animate-fade-in hidden sm:block ${isDark ? 'bg-slate-900/95 border border-slate-700' : 'bg-white/95 border border-slate-200'}`}>
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        className="block"
      />
    </div>
  )
}
